//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';
import React, { useMemo, useState } from 'react';
import { Grid, GridCellRenderer } from 'react-virtualized';
import styled, { useTheme } from 'styled-components';
import { DateUtils } from 'unison';

import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import LabelsSelectCheckboxRow from '@components/labels-select/LabelsSelectCheckboxRow';
import {
  LabelsSearchResolver,
  prefixSearch,
} from '@components/labels-select/LabelsSelectUtils';
import SearchBar from '@components/molecules/search-bar/SearchBar';
import {
  GRID_HEIGHT,
  GRID_WIDTH,
  NUM_COLUMNS,
} from '@legacy-components/content/content-library-labels-modal/constants';
import { processLabels } from '@legacy-components/content/content-library-labels-modal/processLabels';
import { ILabel } from '@models/ILabel';
import { IUnisonDateRange } from '@models/UnisonTypes';
import { debounce } from '@utils/debounce';

export const SEARCH_BAR_TEST_ID = 'search-bar-test-id';

interface LabelsModalContentProps {
  allLabels: ILabel[];
  selectedLabels: PojoSet<string>;
  handleCheckboxClick: (isChecked: boolean, labelValue: string) => void;
  handleSearch?: LabelsSearchResolver;
  dateRange?: IUnisonDateRange;
}

export const LabelsModalContent = ({
  allLabels,
  dateRange,
  handleCheckboxClick,
  handleSearch = prefixSearch,
  selectedLabels,
}: LabelsModalContentProps) => {
  const theme = useTheme();
  const [filteredLabels, setFilteredLabels] = useState<ILabel[]>([]);
  const [searchInput, setSearchInput] = useState<string>();
  const [isSearchEmpty, setIsSearchEmpty] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const processedAllLabels = processLabels(allLabels);

  const updateSearch = useMemo(
    () =>
      debounce(async (searchQuery: string) => {
        setIsLoading(true);

        const labels = await handleSearch(allLabels, searchQuery);
        const processedLabels = processLabels(labels);

        setIsLoading(false);
        setIsSearchEmpty(searchQuery.length === 0);

        setFilteredLabels(processedLabels);
      }, 1000),
    [allLabels],
  );

  const handleSearchInputChange = async (inputVal: string) => {
    setSearchInput(inputVal);
    await updateSearch(inputVal);
  };

  const displayLabels = isSearchEmpty ? processedAllLabels : filteredLabels;

  const renderLabel: GridCellRenderer = ({ columnIndex, rowIndex, style }) => {
    const label = displayLabels[rowIndex * NUM_COLUMNS + columnIndex];
    if (label) {
      const isChecked = PojoSet.has(selectedLabels, label.value);
      const handleClick = () => handleCheckboxClick(!isChecked, label.value);
      return (
        <CheckboxRow
          handleCheckboxClick={handleClick}
          isChecked={isChecked}
          isInlineCount={true}
          label={label}
          style={style}
        />
      );
    } else {
      return null;
    }
  };

  return (
    <>
      {dateRange && (
        <DateRangeContainer>
          Viewing all labels used within this page over{' '}
          {dateRange.alias !== 'custom'
            ? `the ${DateUtils.ALIASES[dateRange.alias].display.toLowerCase()}`
            : `${DateUtils.dateToString(
                dateRange.startDate,
              )} - ${DateUtils.dateToString(dateRange.endDate)}`}
          .
        </DateRangeContainer>
      )}
      <SearchContainer>
        <SearchBar
          handleClear={() => handleSearchInputChange('')}
          onChange={handleSearchInputChange}
          placeholder='Find labels...'
          trackingIdPrefix={SEARCH_BAR_TEST_ID}
          value={searchInput}
          visuallyHiddenLabel='Find labels'
        />
      </SearchContainer>
      <div>
        {isLoading ? (
          <GridContaner>
            <CenteredSpinner />
          </GridContaner>
        ) : (
          <GridContaner>
            {displayLabels.length > 0 ? (
              <StyledGrid
                cellRenderer={renderLabel}
                columnCount={NUM_COLUMNS}
                columnWidth={GRID_WIDTH / 3}
                height={GRID_HEIGHT}
                rowCount={Math.ceil(displayLabels.length / NUM_COLUMNS)}
                rowHeight={parseInt(theme.spacings[4], 10)}
                width={GRID_WIDTH}
              />
            ) : (
              <NoResultsContainer>
                There are no results that match your search.
              </NoResultsContainer>
            )}
          </GridContaner>
        )}
      </div>
    </>
  );
};

const StyledGrid = styled(Grid)`
  &:focus {
    outline: none;
  }
`;

const NoResultsContainer = styled.div`
  height: ${GRID_HEIGHT}px;
`;

const CheckboxRow = styled(LabelsSelectCheckboxRow)(
  ({ theme }) => `
  padding: ${theme.spacings[1]};
`,
);

const DateRangeContainer = styled.div(
  ({ theme }) => `
  margin-bottom: ${theme.spacings[3]};
`,
);

const GridContaner = styled.div`
  display: flex;
  min-height: ${GRID_HEIGHT}px;
  justify-content: center;
  align-items: center;
`;

const SearchContainer = styled.div(
  ({ theme }) => `
  position: relative;
  margin-bottom: ${theme.spacings[3]};
`,
);
